/**
 * Agents Page
 *
 * Displays registered agents with their status, capabilities, and heartbeat info.
 * Uses EITELKEIT tokens for styling.
 */

import { useEffect, useState, type CSSProperties } from 'react';
import { tokens } from '../local-components';
import { useAgent, type RegisteredAgent } from '../hooks/useAgent';

// ═══════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════

const pageStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[6],
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const titleStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes['2xl'],
  fontWeight: tokens.fontWeights.bold,
  color: tokens.colors.textPrimary,
  margin: 0,
};

const buttonStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.void,
  backgroundColor: tokens.colors.mint,
  border: 'none',
  borderRadius: tokens.radii.md,
  padding: `${tokens.spacing[2]} ${tokens.spacing[4]}`,
  cursor: 'pointer',
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  backgroundColor: tokens.colors.surface,
  color: tokens.colors.textMuted,
  cursor: 'not-allowed',
};

const gridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
  gap: tokens.spacing[4],
};

const cardStyle: CSSProperties = {
  backgroundColor: tokens.colors.surface,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.lg,
  padding: tokens.spacing[4],
  display: 'flex',
  flexDirection: 'column',
  gap: tokens.spacing[3],
};

const cardHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const agentNameStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.lg,
  fontWeight: tokens.fontWeights.semibold,
  color: tokens.colors.textPrimary,
};

const statusBadgeStyle = (status: string): CSSProperties => {
  let bgColor = tokens.colors.border;
  let textColor = tokens.colors.textMuted;

  switch (status) {
    case 'healthy':
      bgColor = 'rgba(0, 255, 135, 0.2)';
      textColor = tokens.colors.mint;
      break;
    case 'degraded':
      bgColor = 'rgba(255, 165, 2, 0.2)';
      textColor = tokens.colors.warning;
      break;
    case 'unreachable':
      bgColor = 'rgba(255, 71, 87, 0.2)';
      textColor = tokens.colors.danger;
      break;
  }

  return {
    fontFamily: tokens.fonts.mono,
    fontSize: tokens.fontSizes.xs,
    fontWeight: tokens.fontWeights.medium,
    color: textColor,
    backgroundColor: bgColor,
    padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
    borderRadius: tokens.radii.sm,
    textTransform: 'uppercase',
  };
};

const rowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[2],
};

const labelStyle: CSSProperties = {
  fontFamily: tokens.fonts.sans,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: tokens.colors.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  minWidth: '80px',
};

const valueStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textPrimary,
};

const capabilitiesContainerStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: tokens.spacing[1],
  marginTop: tokens.spacing[1],
};

const capabilityTagStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  color: tokens.colors.textMuted,
  backgroundColor: tokens.colors.void,
  border: `1px solid ${tokens.colors.border}`,
  borderRadius: tokens.radii.sm,
  padding: `2px ${tokens.spacing[2]}`,
};

const loadingStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  padding: tokens.spacing[6],
  textAlign: 'center',
};

const errorStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.danger,
  padding: tokens.spacing[4],
  backgroundColor: 'rgba(255, 71, 87, 0.1)',
  border: `1px solid ${tokens.colors.danger}`,
  borderRadius: tokens.radii.md,
};

const emptyStyle: CSSProperties = {
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.sm,
  color: tokens.colors.textMuted,
  textAlign: 'center',
  padding: tokens.spacing[8],
  backgroundColor: tokens.colors.surface,
  border: `1px dashed ${tokens.colors.border}`,
  borderRadius: tokens.radii.lg,
};

const modeBadgeStyle = (mode: string): CSSProperties => ({
  fontFamily: tokens.fonts.mono,
  fontSize: tokens.fontSizes.xs,
  fontWeight: tokens.fontWeights.medium,
  color: mode === 'root' ? tokens.colors.mint : tokens.colors.info,
  backgroundColor: mode === 'root' ? 'rgba(0, 255, 135, 0.1)' : 'rgba(59, 130, 246, 0.1)',
  padding: `2px ${tokens.spacing[2]}`,
  borderRadius: tokens.radii.sm,
});

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════

function formatRelativeTime(dateString: string | null): string {
  if (dateString === null) return 'Never';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return `${diffSecs}s ago`;
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// ═══════════════════════════════════════════════════════════════
// AGENT CARD
// ═══════════════════════════════════════════════════════════════

interface AgentCardProps {
  agent: RegisteredAgent;
}

function AgentCard({ agent }: AgentCardProps) {
  return (
    <div
      style={cardStyle}
      data-testid="agent-item"
      data-agent-id={agent.id}
      data-status={agent.status}
    >
      {/* Header */}
      <div style={cardHeaderStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
          <span style={agentNameStyle} data-testid="agent-hostname">
            {agent.hostname}
          </span>
          <span style={modeBadgeStyle(agent.mode)}>{agent.mode}</span>
        </div>
        <span style={statusBadgeStyle(agent.status)}>{agent.status}</span>
      </div>

      {/* Details */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacing[2] }}>
        <div style={rowStyle}>
          <span style={labelStyle}>ID</span>
          <span style={valueStyle}>{agent.id.slice(0, 8)}...</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Endpoint</span>
          <span style={valueStyle}>{agent.endpoint}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Version</span>
          <span style={valueStyle}>{agent.version}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Registered</span>
          <span style={valueStyle}>{formatRelativeTime(agent.registeredAt)}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>Heartbeat</span>
          <span style={valueStyle}>{formatRelativeTime(agent.lastHeartbeat)}</span>
        </div>
      </div>

      {/* Capabilities */}
      <div>
        <span style={labelStyle}>
          Capabilities (<span data-testid="agent-capabilities">{agent.capabilities.length}</span>)
        </span>
        <div style={capabilitiesContainerStyle}>
          {agent.capabilities.slice(0, 10).map((cap) => (
            <span key={cap} style={capabilityTagStyle}>
              {cap}
            </span>
          ))}
          {agent.capabilities.length > 10 && (
            <span style={{ ...capabilityTagStyle, color: tokens.colors.textMuted }}>
              +{agent.capabilities.length - 10} more
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════

export function Agents() {
  const { isConnected, listAgents } = useAgent();
  const [agents, setAgents] = useState<RegisteredAgent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAgents = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await listAgents();
      setAgents(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch agents');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      fetchAgents();
    }
  }, [isConnected]);

  const handleRefresh = () => {
    fetchAgents();
  };

  if (!isConnected) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Agents</h1>
        <div style={loadingStyle}>Connecting to agent...</div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={pageStyle}>
        <h1 style={titleStyle}>Agents</h1>
        <div style={loadingStyle}>Loading agents...</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <h1 style={titleStyle}>Agents</h1>
        <button
          onClick={handleRefresh}
          disabled={!isConnected}
          style={isConnected ? buttonStyle : buttonDisabledStyle}
          data-testid="agent-refresh-btn"
        >
          Refresh
        </button>
      </div>

      {/* Error */}
      {error !== null && <div style={errorStyle}>Error: {error}</div>}

      {/* Agent Grid */}
      {agents.length === 0 ? (
        <div style={emptyStyle}>No agents registered. Start a brutal-agent to see it here.</div>
      ) : (
        <div style={gridStyle}>
          {agents.map((agent) => (
            <AgentCard key={agent.id} agent={agent} />
          ))}
        </div>
      )}
    </div>
  );
}
